import { Fragment, useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '@lib/api/client';
import { computeHoursStats } from '../../lib/hoursStats';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';
import { FinancialContent } from './FinancialPage';
import { enqueue, isNetworkError, initOfflineSync, queuedCount } from '@/lib/offlineQueue';

interface EntryLine { id?: number; job_id: number | null; description: string; hours: string }
interface Entry {
  id: number; work_date: string; hours: number; break_minutes: number;
  notes?: string; status: 'draft' | 'submitted' | 'approved' | 'rejected';
  disputed?: number; dispute_note?: string | null;
  job_title?: string;
  lines: { id: number; job_id: number | null; job_title?: string; site_address?: string; description?: string; hours: number }[];
}
interface ScheduledJob { schedule_id?: number | null; job_id: number; job_title: string; site_address?: string }
interface DayTask {
  id: number; title: string; description?: string | null;
  status: string; job_id: number; job_title: string;
  approval_status?: string;
  checklist?: { id: number; text: string; checked: number }[];
}

// Draft persistence key for offline-friendly save (#8)
const DRAFT_KEY = 'ts_draft';

const statusColor = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-100 text-blue-700',
  approved:  'bg-green-100 text-green-700',
  rejected:  'bg-red-100 text-red-700',
};

export const TimesheetPage = () => {
  const [params] = useSearchParams();
  const [view, setView] = useState<'hours' | 'money'>(params.get('tab') === 'money' ? 'money' : 'hours');
  const [entries, setEntries]           = useState<Entry[]>([]);
  const [pendingSync, setPendingSync]   = useState(queuedCount());
  const [scheduledJobs, setScheduled]   = useState<ScheduledJob[]>([]);
  const [loading, setLoading]           = useState(true);
  const [workDate, setWorkDate]         = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime]       = useState('');
  const [endTime, setEndTime]           = useState('');
  const [lines, setLines]               = useState<EntryLine[]>([{ job_id: null, description: '', hours: '' }]);
  const [lunchDeducted, setLunch]       = useState(true);
  const [notes, setNotes]               = useState('');
  const [busy, setBusy]                 = useState(false);
  const [msg, setMsg]                   = useState('');
  const [error, setError]               = useState<string | null>(null);
  // #8 – track if form has unsaved offline draft
  const [hasDraft, setHasDraft]         = useState(false);
  const [openEntry, setOpenEntry]       = useState<number | null>(null);
  const [dayTasks, setDayTasks]         = useState<DayTask[]>([]);
  const [checkedTasks, setCheckedTasks] = useState<Set<number>>(new Set());
  const [monthAnchor, setMonthAnchor]   = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });

  // #8 – restore draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.lines) setLines(d.lines);
        if (d.notes !== undefined) setNotes(d.notes);
        if (d.workDate) setWorkDate(d.workDate);
        if (d.lunchDeducted !== undefined) setLunch(d.lunchDeducted);
        setHasDraft(true);
      }
    } catch { /* ignore corrupt storage */ }
  }, []);

  // #8 – persist draft to localStorage whenever form changes
  useEffect(() => {
    const hasData = lines.some(l => l.hours || l.description);
    if (hasData) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ lines, notes, workDate, lunchDeducted }));
      setHasDraft(true);
    }
  }, [lines, notes, workDate, lunchDeducted]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const e = await apiClient.get<{ success: boolean; data: Entry[] }>('/portal/me/time-entries');
      setEntries(e.data.data ?? []);
    } catch { setError('Failed to load timesheet. Please refresh.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Jobs for the picked day — the day's SCHEDULED job auto-selects on the first
  // line so hours land on the right job (the dropdown still lists everything).
  useEffect(() => {
    let alive = true;
    apiClient.get<{ success: boolean; data: ScheduledJob[] }>(`/portal/me/scheduled-jobs-today?date=${workDate}`)
      .then(r => {
        if (!alive) return;
        const jobs = r.data.data ?? [];
        setScheduled(jobs);
        const dayJob = jobs.find(j => j.schedule_id) || (jobs.length === 1 ? jobs[0] : null);
        if (dayJob) setLines(prev => prev.map((l, i) => i === 0 ? { ...l, job_id: dayJob.job_id } : l));
      })
      .catch(() => {});
    // The day's plan-board tasks — checked off as part of logging hours
    setCheckedTasks(new Set());
    apiClient.get<{ success: boolean; data: DayTask[] }>(`/portal/me/day-tasks?date=${workDate}`)
      .then(r => { if (alive) setDayTasks(r.data.data ?? []); })
      .catch(() => { if (alive) setDayTasks([]); });
    return () => { alive = false; };
  }, [workDate]);

  const toggleTask = (id: number) => setCheckedTasks(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const addLine = () => setLines(p => [...p, { job_id: null, description: '', hours: '' }]);
  const removeLine = (i: number) => setLines(p => p.filter((_, idx) => idx !== i));
  const updateLine = (i: number, field: keyof EntryLine, value: string | number | null) =>
    setLines(p => p.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const usingClock = !!(startTime && endTime);
  const clockHours = (start: string, end: string, breakMin: number) => {
    const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
    let mins = toMin(end) - toMin(start);
    if (mins < 0) mins += 1440;
    mins -= breakMin;
    return Math.max(0, Math.round((mins / 60) * 100) / 100);
  };
  const totalHours = lines.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
  const netHours   = usingClock
    ? clockHours(startTime, endTime, lunchDeducted ? 30 : 0)
    : Math.max(0, totalHours - (lunchDeducted ? 0.5 : 0));

  useEffect(() => {
    initOfflineSync(() => { setPendingSync(queuedCount()); reload(); });
    const onOnline = () => setPendingSync(queuedCount());
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (submit: boolean) => {
    const hasLineHours = lines.some(l => l.hours && parseFloat(l.hours) > 0);
    // A submitted day needs a note about what was done (drafts can skip it)
    if (submit && !notes.trim() && !lines.some(l => l.description.trim()) && checkedTasks.size === 0) {
      setMsg('Check off plan tasks or describe what you completed before submitting.'); return;
    }
    if (usingClock) {
      if (netHours <= 0) { setMsg('End time must be after start time.'); return; }
    } else if (!hasLineHours) {
      setMsg('Enter start & end time, or hours for each line.'); return;
    }
    // With clock times, hours come from the span; allocate to job lines (default
    // the whole day to the first line's job if no explicit split is entered).
    const outLines = usingClock && !hasLineHours
      ? [{ job_id: lines[0].job_id ?? undefined, description: lines[0].description || undefined, hours: netHours }]
      : lines.map(l => ({ job_id: l.job_id ?? undefined, description: l.description || undefined, hours: parseFloat(l.hours) }));
    setBusy(true);
    setMsg('');
    try {
      await apiClient.post('/portal/me/time-entries', {
        work_date: workDate,
        hours: usingClock ? netHours : totalHours,
        break_minutes: lunchDeducted ? 30 : 0,
        notes: notes || undefined,
        submit,
        start_time: usingClock ? startTime : undefined,
        end_time: usingClock ? endTime : undefined,
        lines: outLines,
        completed_task_ids: [...checkedTasks],
      });
      setMsg(submit ? 'Entry submitted for approval.' : 'Draft saved.');
      setLines([{ job_id: null, description: '', hours: '' }]);
      setStartTime(''); setEndTime('');
      setNotes('');
      setCheckedTasks(new Set());
      apiClient.get<{ success: boolean; data: DayTask[] }>(`/portal/me/day-tasks?date=${workDate}`)
        .then(r => setDayTasks(r.data.data ?? [])).catch(() => {});
      clearDraft();
      reload();
    } catch (err) {
      if (isNetworkError(err)) {
        enqueue('/portal/me/time-entries', {
          work_date: workDate,
          hours: usingClock ? netHours : totalHours,
          break_minutes: lunchDeducted ? 30 : 0,
          notes: notes || undefined,
          submit,
          start_time: usingClock ? startTime : undefined,
          end_time: usingClock ? endTime : undefined,
          lines: outLines,
          completed_task_ids: [...checkedTasks],
        });
        setPendingSync(queuedCount());
        setMsg('No signal — saved on this phone, will send itself when you\'re back in coverage.');
        setLines([{ job_id: null, description: '', hours: '' }]);
        setStartTime(''); setEndTime('');
        setNotes('');
        setCheckedTasks(new Set());
        clearDraft();
      } else {
        setMsg('Failed to add entry.');
      }
    }
    finally { setBusy(false); }
  };

  const doSubmit = async (id: number) => {
    setBusy(true);
    try {
      await apiClient.post(`/portal/me/time-entries/${id}/submit`, {});
      reload();
    } finally { setBusy(false); }
  };

  // Flag a submitted/approved entry as wrong — the office gets a "disputed" badge
  const doDispute = async (id: number) => {
    const note = window.prompt("What's wrong with this entry? (e.g. worked 8h not 6h)");
    if (!note?.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/portal/me/time-entries/${id}/dispute`, { note: note.trim() });
      setMsg('Sent — the office will review this entry.');
      reload();
    } catch {
      setMsg('Could not send the dispute.');
    } finally { setBusy(false); }
  };

  // Remove a wrong draft/rejected entry (approved/submitted are locked)
  const doDelete = async (id: number) => {
    if (!window.confirm('Delete this time entry?')) return;
    setBusy(true);
    try {
      await apiClient.delete(`/portal/me/time-entries/${id}`);
      reload();
    } catch {
      setMsg('Could not delete that entry.');
    } finally { setBusy(false); }
  };

  // #5 – duplicate yesterday's entry
  const duplicateYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().slice(0, 10);
    const prev = entries.find(e => e.work_date === yStr);
    if (!prev) { setMsg('No entry found for yesterday.'); return; }
    setWorkDate(new Date().toISOString().slice(0, 10));
    if (prev.lines?.length) {
      setLines(prev.lines.map(l => ({ job_id: l.job_id ?? null, description: l.description ?? '', hours: String(l.hours) })));
    } else {
      setLines([{ job_id: prev.lines?.[0]?.job_id ?? null, description: '', hours: String(prev.hours) }]);
    }
    setLunch(prev.break_minutes >= 30);
    setNotes(prev.notes ?? '');
    setMsg("Yesterday's entry prefilled — edit if needed.");
  };

  if (loading) return <div className="h-24 animate-pulse rounded-xl bg-gray-100" />;
  if (error) return (
    <div className="px-4 py-4 text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200">
      <span className="material-icons text-base">error</span>{error}
    </div>
  );

  return (
    <PullToRefresh onRefresh={reload}>
    <div className="space-y-5">
      <LargeTitleHeader title="Hours & Pay" />

      {/* Hours | Money switch — one tab, both worlds */}
      <div className="grid grid-cols-2 gap-1 bg-gray-100 rounded-xl p-1">
        {(['hours', 'money'] as const).map(v => (
          <button key={v} onClick={() => setView(v)}
            className={`h-9 rounded-lg text-sm font-bold capitalize flex items-center justify-center gap-1.5 ${view === v ? 'bg-white text-[#C77D3F] shadow-sm' : 'text-gray-500'}`}>
            <span className="material-symbols-outlined text-lg">{v === 'hours' ? 'schedule' : 'account_balance_wallet'}</span>
            {v}
          </button>
        ))}
      </div>

      {view === 'money' ? <FinancialContent /> : (
      <>

      {/* Hours at a glance */}
      {(() => {
        const h = computeHoursStats(entries);
        const cell = (label: string, val: string) => (
          <div className="text-center">
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className="text-base font-bold text-gray-900">{val}</p>
          </div>
        );
        return (
          <div className="bg-white rounded-xl border border-gray-200 px-3 py-2.5 grid grid-cols-4 divide-x divide-gray-100">
            {cell('Today', `${h.today.toFixed(1)}h`)}
            {cell('This week', `${h.thisWeek.toFixed(1)}h`)}
            {cell('This month', `${h.thisMonth.toFixed(1)}h`)}
            {cell('Pending', `${h.pendingHours.toFixed(1)}h`)}
          </div>
        );
      })()}

      {/* Pick-a-day calendar — tap a date to log for it; logged days show totals */}
      {(() => {
        const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const todayStr = ymd(new Date());
        const byDay = new Map<string, Entry[]>();
        for (const e of entries) {
          const k = (e.work_date || '').slice(0, 10);
          if (!byDay.has(k)) byDay.set(k, []);
          byDay.get(k)!.push(e);
        }
        const y = monthAnchor.getFullYear(), mo = monthAnchor.getMonth();
        const offset = new Date(y, mo, 1).getDay(); // Sunday-first (S M T W T F S)
        const cursor = new Date(y, mo, 1 - offset);
        const cells: Date[] = [];
        for (let i = 0; i < 42; i++) { cells.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }
        const selEntries = byDay.get(workDate) || [];
        return (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-2 pt-2">
              <button onClick={() => setMonthAnchor(pm => new Date(pm.getFullYear(), pm.getMonth() - 1, 1))}
                className="h-9 w-9 rounded-lg text-gray-500 flex items-center justify-center active:bg-gray-100">
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="text-sm font-bold text-gray-900">
                {monthAnchor.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={() => setMonthAnchor(pm => new Date(pm.getFullYear(), pm.getMonth() + 1, 1))}
                className="h-9 w-9 rounded-lg text-gray-500 flex items-center justify-center active:bg-gray-100">
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
            <div className="grid grid-cols-7 text-center text-[10px] font-bold text-gray-400 uppercase">
              {['S','M','T','W','T','F','S'].map((w, i) => <div key={i} className="py-1">{w}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {cells.map(d => {
                const key = ymd(d);
                const inMonth = d.getMonth() === mo;
                const dayEntries = byDay.get(key) || [];
                const tot = dayEntries.reduce((sum, e) => sum + Number(e.hours), 0);
                const isSel = key === workDate;
                const isToday = key === todayStr;
                return (
                  <button key={key} onClick={() => setWorkDate(key)}
                    className={`h-12 flex flex-col items-center justify-center gap-0.5 border-t border-r border-gray-50 last:border-r-0
                      ${inMonth ? '' : 'opacity-30'} ${isSel ? 'bg-[#C77D3F]/15' : 'active:bg-gray-50'}`}>
                    <span className={`text-xs leading-none flex items-center justify-center rounded-full h-5 w-5
                      ${isToday ? 'bg-[#C77D3F] text-white font-bold' : isSel ? 'text-[#C77D3F] font-bold' : 'text-gray-700'}`}>
                      {d.getDate()}
                    </span>
                    {tot > 0 && <span className="text-[9px] font-bold text-green-700 leading-none">{tot.toFixed(1)}h</span>}
                  </button>
                );
              })}
            </div>
            {/* Selected day's logged work: start–end · job · hours */}
            {selEntries.length > 0 && (
              <div className="border-t border-gray-100 px-3 py-2 space-y-1">
                {selEntries.map(e => (
                  <div key={e.id} className="flex items-center gap-2 text-xs">
                    <span className="material-symbols-outlined text-sm text-green-600">check_circle</span>
                    {(e as Entry & { start_time?: string; end_time?: string }).start_time && (
                      <span className="text-gray-500">
                        {String((e as Entry & { start_time?: string }).start_time).slice(0, 5)}–{String((e as Entry & { end_time?: string }).end_time || '').slice(0, 5)}
                      </span>
                    )}
                    <span className="text-gray-700 truncate flex-1">{e.job_title || e.lines?.[0]?.job_title || 'No job'}</span>
                    <span className="font-bold text-gray-900">{Number(e.hours).toFixed(1)}h</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusColor[e.status]}`}>{e.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Log hours form */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-sm">Log Hours</h2>
          <span className="text-sm font-medium text-[#C77D3F]">
            {new Date(workDate + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
        </div>

        {/* Clock in/out — hours are computed from these when both are set */}
        <div className="flex items-end gap-3">
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-xs text-gray-500">Start</span>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
          </label>
          <label className="flex flex-col gap-1 flex-1">
            <span className="text-xs text-gray-500">End</span>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
          </label>
          {usingClock && (
            <div className="text-sm text-gray-600 pb-1.5 whitespace-nowrap">= <span className="font-bold text-gray-900">{netHours.toFixed(2)} h</span></div>
          )}
        </div>

        {/* #8 – draft restore banner */}
        {hasDraft && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-xs text-amber-700 flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">edit_note</span>
              Unsaved draft restored
            </span>
            <button onClick={() => { setLines([{ job_id: null, description: '', hours: '' }]); setNotes(''); clearDraft(); }}
              className="text-xs text-amber-600 hover:underline font-medium">Discard</button>
          </div>
        )}

        {pendingSync > 0 && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-base">cloud_off</span>
            {pendingSync} entr{pendingSync === 1 ? 'y' : 'ies'} saved offline — sends automatically when you're back in signal.
          </p>
        )}
        {msg && <p className={`text-sm ${msg.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>{msg}</p>}

        {/* #5 – duplicate yesterday shortcut */}
        <button onClick={duplicateYesterday}
          className="flex items-center gap-1 text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-[#C77D3F] hover:text-[#C77D3F] transition-colors">
          <span className="material-symbols-outlined text-sm">content_copy</span>
          Duplicate yesterday's entry
        </button>

        {/* Job lines */}
        <div className="space-y-3">
          {lines.map((line, i) => (
            <div key={i} className="space-y-1.5 border border-gray-100 rounded-lg p-2.5 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">{lines.length > 1 ? `Job site ${i + 1}` : 'Job'}</span>
                {lines.length > 1 && (
                  <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-500">
                    <span className="material-icons text-sm">close</span>
                  </button>
                )}
              </div>
              <select
                value={line.job_id ?? ''}
                onChange={e => updateLine(i, 'job_id', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none"
              >
                <option value="">— Select job (optional)</option>
                {scheduledJobs.map(j => (
                  <option key={j.job_id} value={j.job_id}>{j.job_title}{j.site_address ? ` — ${j.site_address}` : ''}</option>
                ))}
              </select>
              <input
                type="text"
                value={line.description}
                onChange={e => updateLine(i, 'description', e.target.value)}
                placeholder={lines.length > 1 ? 'What was done at this site' : 'Work description (optional)'}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              />
              {(lines.length > 1 || !usingClock) && (
                <input
                  type="number" min="0.25" max="16" step="0.25"
                  value={line.hours}
                  onChange={e => updateLine(i, 'hours', e.target.value)}
                  placeholder={lines.length > 1 ? 'Hours at this site' : 'Hours'}
                  className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none"
                />
              )}
            </div>
          ))}
        </div>

        <button onClick={addLine} className="flex items-center gap-1 text-xs text-[#C77D3F] font-medium hover:underline">
          <span className="material-icons text-sm">add</span>+ Job (worked at another site today?)
        </button>

        {/* #3 – lunch deduction toggle (quick and prominent) */}
        <label className="flex items-center gap-3 cursor-pointer py-1">
          <div
            onClick={() => setLunch(p => !p)}
            className={`relative w-10 h-6 rounded-full transition-colors ${lunchDeducted ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${lunchDeducted ? 'translate-x-4' : ''}`} />
          </div>
          <span className="text-sm text-gray-700">Deduct 30-min unpaid lunch</span>
        </label>

        {/* The day's plan — check off what got done (feeds the office board) */}
        {dayTasks.length > 0 && (
          <div className="border border-[#C77D3F]/25 bg-[#FAF6F0] rounded-xl p-3 space-y-1">
            <p className="text-xs font-bold text-[#7a4a22] flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base text-[#C77D3F]">view_kanban</span>
              Today's plan — check off what you finished
            </p>
            {dayTasks.map(t => {
              const done = t.status === 'completed';
              const awaiting = t.approval_status === 'pending';
              const checked = done || checkedTasks.has(t.id);
              return (
                <label key={t.id} className={`flex items-start gap-2 py-1 ${done || awaiting ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input type="checkbox" checked={checked} disabled={done || awaiting}
                    onChange={() => toggleTask(t.id)} className="accent-[#C77D3F] mt-0.5" />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm leading-snug ${checked ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</span>
                    <span className="block text-[10px] text-gray-400 truncate">
                      {t.job_title}{done ? ' · already done' : awaiting ? ' · your suggestion — awaiting office approval' : ''}
                    </span>
                    {(t.checklist?.length || 0) > 0 && (
                      <span className="block mt-0.5 space-y-0.5">
                        {t.checklist!.map(ci => (
                          <label key={ci.id} className="flex items-center gap-1.5 cursor-pointer" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={!!ci.checked} disabled={awaiting}
                              onChange={async e => {
                                const next = e.target.checked;
                                setDayTasks(ts => ts.map(x => x.id === t.id
                                  ? { ...x, checklist: x.checklist!.map(y => y.id === ci.id ? { ...y, checked: next ? 1 : 0 } : y) } : x));
                                try { await apiClient.post(`/portal/me/checklist/${ci.id}`, { checked: next }); } catch { /* refetch on next day change */ }
                              }}
                              className="accent-[#5C7F4F] scale-90" />
                            <span className={`text-[11px] ${ci.checked ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{ci.text}</span>
                          </label>
                        ))}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        <label className="block">
          <span className="text-xs font-medium text-gray-600">Tasks completed <span className="text-red-500">*</span></span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="What did you get done today? e.g. Set A01–A08 trusses, sheeted east roof"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
        </label>

        {/* Summary */}
        <div className="flex items-center gap-4 text-sm py-1 border-t border-gray-100">
          <span className="text-gray-500">Gross: <strong>{totalHours.toFixed(2)}h</strong></span>
          {lunchDeducted && <span className="text-gray-400">−0.5h lunch</span>}
          <span className="font-semibold text-[#C77D3F]">Net: {netHours.toFixed(2)}h</span>
        </div>

        {/* #19 – sticky action row */}
        <div className="flex gap-2 sticky bottom-20 pt-1">
          <button onClick={() => add(false)} disabled={busy || netHours <= 0}
            className="border border-gray-300 text-gray-700 rounded-lg px-4 py-2.5 text-sm hover:bg-gray-50 disabled:opacity-50 flex-1">
            Save Draft
          </button>
          <button onClick={() => add(true)} disabled={busy || netHours <= 0}
            className="bg-[#C77D3F] text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50 flex-2 flex-grow active:scale-95 transition-transform">
            Submit
          </button>
        </div>
      </div>

      {/* Logged entries — compact table; tap a row for details & actions */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-3 pt-3 pb-1 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-sm">Logged hours</h2>
          <span className="text-[10px] text-gray-400">tap a row for details</span>
        </div>
        {entries.length === 0 ? (
          <div className="p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-4xl">schedule</span>
            <p className="text-sm text-gray-500 mt-2">No entries yet — log your first hours above.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-gray-400 border-b border-gray-100">
                <th className="px-3 py-1.5 font-semibold">Date</th>
                <th className="py-1.5 font-semibold">Job</th>
                <th className="py-1.5 font-semibold text-right">Hrs</th>
                <th className="py-1.5 pl-2 pr-3 font-semibold text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.map(e => {
                const jobName = e.lines?.[0]?.job_title || e.job_title || '—';
                const extra = (e.lines?.length || 0) > 1 ? ` +${e.lines.length - 1}` : '';
                const open = openEntry === e.id;
                return (
                  <Fragment key={e.id}>
                    <tr onClick={() => setOpenEntry(o => o === e.id ? null : e.id)} className={`cursor-pointer ${open ? 'bg-[#C77D3F]/5' : 'active:bg-gray-50'}`}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                        {new Date(e.work_date.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="py-2 text-gray-600 max-w-[7.5rem]"><span className="block truncate">{jobName}{extra}</span></td>
                      <td className="py-2 text-right font-bold text-[#C77D3F] whitespace-nowrap">{Number(e.hours).toFixed(2)}</td>
                      <td className="py-2 pl-2 pr-3 text-right whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusColor[e.status]}`}>{e.status}</span>
                        {!!e.disputed && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700" title={e.dispute_note || ''}>!</span>}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={4} className="bg-gray-50 px-3 py-2.5">
                          {(e as Entry & { start_time?: string; end_time?: string }).start_time && (
                            <p className="text-[11px] text-gray-500 mb-1">
                              Clock: {String((e as Entry & { start_time?: string }).start_time).slice(0, 5)}–{String((e as Entry & { end_time?: string }).end_time || '').slice(0, 5)}
                              {e.break_minutes > 0 ? ` · ${e.break_minutes}m break` : ''}
                            </p>
                          )}
                          {e.lines?.length > 0 && (
                            <div className="space-y-0.5 mb-1">
                              {e.lines.map((l, idx) => (
                                <div key={idx} className="flex items-start justify-between text-[11px]">
                                  <span className="text-gray-600">{l.job_title || 'No job'}{l.description ? ` — ${l.description}` : ''}</span>
                                  <span className="font-mono font-semibold text-gray-700 ml-2 flex-shrink-0">{Number(l.hours).toFixed(2)}h</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {e.notes && <p className="text-[11px] text-gray-500 italic mb-1.5">"{e.notes}"</p>}
                          {!!e.disputed && e.dispute_note && <p className="text-[11px] text-red-600 mb-1.5">Disputed: {e.dispute_note}</p>}
                          <div className="flex gap-1.5">
                            {e.status === 'draft' && (
                              <button onClick={() => doSubmit(e.id)} disabled={busy}
                                className="h-8 px-3 rounded-lg bg-[#C77D3F] text-white text-[11px] font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-sm">send</span> Submit
                              </button>
                            )}
                            {(e.status === 'draft' || e.status === 'rejected') && (
                              <button onClick={() => doDelete(e.id)} disabled={busy}
                                className="h-8 px-3 rounded-lg border border-red-200 text-red-600 text-[11px] font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-sm">delete</span> Delete
                              </button>
                            )}
                            {(e.status === 'submitted' || e.status === 'approved') && !e.disputed && (
                              <button onClick={() => doDispute(e.id)} disabled={busy}
                                className="h-8 px-3 rounded-lg border border-gray-300 text-gray-600 text-[11px] font-bold flex items-center gap-1">
                                <span className="material-symbols-outlined text-sm">flag</span> Dispute
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
    </PullToRefresh>
  );

};
